<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Text Field Component
      p.text--secondary A versatile text field component with multiple states and configurations.

    // Text Field States
    .component-section.mb-8#basic-states
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-states')") Basic States
      .variations-grid
        .variation-item.mb-6(v-for="variation in textFieldVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          // Grouped text fields (multiple examples)
          template(v-if="variation.isGroup && variation.id === 'slots'")
            .variation-preview.mb-4.pa-4.border.rounded
              .d-flex.flex-column
                // Slot examples with custom rendering
                div(v-for="field in variation.fields" :key="field.label" class="mb-4")
                  div.mb-2
                    span.font-weight-medium {{ field.label }}
                    small.text--secondary.ml-2(v-if="field.hint") {{ field.hint }}

                  // Append slot example
                  yz-text-field(v-if="field.key === 'slotAppend'" :value="field.value" @input="(val) => $set(fieldValues, field.key, val)" v-bind="field.props" :style="{ maxWidth: '400px' }")
                    template(#append)
                      yz-icon(v-if="field.value" color="yz-success-500") $yzi-check-circle

                  // Prepend slot example
                  yz-text-field(v-else-if="field.key === 'slotPrepend'" :value="field.value" @input="(val) => $set(fieldValues, field.key, val)" v-bind="field.props" :style="{ maxWidth: '400px' }")
                    template(#prepend)
                      yz-icon(color="yz-neutral-500") $yzi-user-01

                  // Prepend inner slot example
                  yz-text-field(v-else-if="field.key === 'slotPrependInner'" :value="field.value" @input="(val) => $set(fieldValues, field.key, val)" v-bind="field.props" :style="{ maxWidth: '400px' }")
                    template(#prepend-inner)
                      yz-icon(color="yz-neutral-500") $yzi-search-sm

                  // Append outer slot example
                  yz-text-field(v-else-if="field.key === 'slotAppendOuter'" :value="field.value" @input="(val) => $set(fieldValues, field.key, val)" v-bind="field.props" :style="{ maxWidth: '400px' }")
                    template(#append-outer)
                      yz-btn(icon-only size="sm" icon="$yzi-plus" @click="handleSlotAction('add-note')")

                  // Multiple slots example
                  yz-text-field(v-else-if="field.key === 'slotMultiple'" :value="field.value" @input="(val) => $set(fieldValues, field.key, val)" v-bind="field.props" :style="{ maxWidth: '400px' }")
                    template(#prepend-inner)
                      yz-icon(color="yz-neutral-500") $yzi-search-sm
                    template(#append)
                      yz-icon(v-if="field.value" color="yz-success-500") $yzi-check-circle
                    template(#append-outer)
                      yz-btn(icon-only size="sm" icon="$yzi-filter-lines" @click="handleSlotAction('toggle-filters')")

                  // Conditional slot example
                  yz-text-field(v-else-if="field.key === 'slotConditional'" :value="field.value" @input="(val) => $set(fieldValues, field.key, val)" v-bind="field.props" :style="{ maxWidth: '400px' }")
                    template(#append)
                      yz-icon(v-if="isValidEmail(field.value)" color="yz-success-500") $yzi-check-circle
                      yz-icon(v-else-if="field.value && !isValidEmail(field.value)" color="yz-danger-500") $yzi-x-circle

                  .mt-2.text-caption.text--secondary
                    strong v-model value:
                    code.pa-1.rounded(style="background-color: #f0f0f0;") "{{ field.value }}"

          // Other grouped text fields (non-slot examples)
          template(v-else-if="variation.isGroup")
            .variation-preview.mb-4.pa-4.border.rounded
              .d-flex.flex-column
                div(v-for="field in variation.fields" :key="field.label" class="mb-4")
                  div.mb-2
                    span.font-weight-medium {{ field.label }}
                    small.text--secondary.ml-2(v-if="field.hint") {{ field.hint }}
                  yz-text-field(:value="field.value" @input="(val) => $set(fieldValues, field.key, val)" v-bind="field.props" @click:append="field.clickHandler && field.clickHandler()" :style="variation.id === 'width-control' ? {} : { maxWidth: '400px' }")
                  .mt-2.text-caption.text--secondary
                    strong v-model value:
                    code.pa-1.rounded(style="background-color: #f0f0f0;") "{{ field.value }}"

            .variation-code
              .code-header.d-flex.align-center.mb-2
                span.font-weight-medium Code Examples

              .code-examples
                div(v-for="field in variation.fields" :key="field.label" class="mb-3")
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === field.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === field.code ? 'success' : ''" @click="copyCode(field.code)")
                    span.font-weight-medium.ml-2 {{ field.label }}
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ field.code }}

          // V-form integration examples
          template(v-else-if="variation.id === 'vform-integration'")
            .variation-preview.mb-4.pa-4.border.rounded
              .mb-4
                h5.mb-2 V-form with Multiple Text Fields
                v-form(ref="demoTextFieldForm")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Full Name
                    yz-text-field(v-model="vformNameValue" placeholder="Enter your full name..." required, required-error-message="Full name is required" :rules="[v => (v && v.length >= 2) || 'Name must be at least 2 characters']" clearable :style="{ maxWidth: '400px' }")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Email Address
                    yz-text-field(v-model="vformEmailValue" placeholder="Enter your email..." required, required-error-message="Email is required" type="email" clearable, :style="{ maxWidth: '400px' }" :rules="[v => /.+@.+\..+/.test(v) || 'Email must be valid']")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Password
                    yz-text-field(v-model="vformPasswordValue" placeholder="Enter password..." :type="showVformPassword ? 'text' : 'password'" :append-icon="showVformPassword ? '$yzi-eye' : '$yzi-slash-circle-01'" required, required-error-message="Password is required" :rules="[v => (v && v.length >= 8) || 'Password must be at least 8 characters']" clearable @click:append="showVformPassword = !showVformPassword" :style="{ maxWidth: '400px' }")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Tel type
                    yz-text-field(v-model="vformPhoneValue" placeholder="Enter phone number..." type="tel" required, required-error-message="Phone number is required" :rules="[v => (v && v.length >= 10) || 'Phone number must be at least 10 digits']" clearable :style="{ maxWidth: '400px' }")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Age
                    yz-text-field(v-model="vformAgeValue" placeholder="Enter your age..." type="number" required, required-error-message="Age is required" :rules="[v => (v && v >= 18) || 'Must be 18 or older', v => (v && v <= 120) || 'Please enter a valid age']" clearable :style="{ maxWidth: '400px' }")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Birth Date
                    yz-text-field(v-model="vformBirthDateValue" type="date" required, required-error-message="Birth date is required" :rules="[v => (v && new Date(v) <= new Date()) || 'Birth date cannot be in the future']" clearable :style="{ maxWidth: '400px' }")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Preferred Contact Time
                    yz-text-field(v-model="vformContactTimeValue" type="time" required, required-error-message="Contact time is required" :rules="[v => (v && v >= '09:00' && v <= '17:00') || 'Contact time must be between 9 AM and 5 PM']" clearable :style="{ maxWidth: '400px' }")
                  .mt-4
                    .d-flex.gap-2
                      yz-btn(label="Reset" size="sm" @click="resetTextFieldVForm")
                      yz-btn(label="Validate" size="sm" outlined @click="validateTextFieldVForm")
                    .mt-2(v-if="vformTextFieldValidationResult")
                      span.yz-regular-body-xs(:class="{ 'success--text': vformTextFieldValidationResult === 'Valid', 'error--text': vformTextFieldValidationResult === 'Invalid' }") {{ vformTextFieldValidationResult }}
                  .mt-3
                    small.text--secondary Form Values: {{ JSON.stringify({ name: vformNameValue, email: vformEmailValue, password: '***', phone: vformPhoneValue, age: vformAgeValue, birthDate: vformBirthDateValue, contactTime: vformContactTimeValue }) }}

            .variation-code
              .code-header.d-flex.align-center.mb-2
                span.font-weight-medium Code Examples

              .code-examples
                .mb-3
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === vformTextFieldExampleCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === vformTextFieldExampleCode ? 'success' : ''" @click="copyCode(vformTextFieldExampleCode)")
                    span.font-weight-medium.ml-2 V-form Integration Example
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ vformTextFieldExampleCode }}

          // Rules-based validation examples
          template(v-else-if="variation.id === 'rules-validation'")
            .variation-preview.mb-4.pa-4.border.rounded
              .mb-4
                h5.mb-2 Custom Validation Rules
                v-form(ref="rulesValidationForm")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Username
                    yz-text-field(v-model="rulesUsernameValue" placeholder="Enter username..." :rules="customUsernameRules" clearable :style="{ maxWidth: '400px' }")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Confirm Password
                    yz-text-field(v-model="rulesConfirmPasswordValue" placeholder="Confirm password..." type="password" :rules="confirmPasswordRules" clearable :style="{ maxWidth: '400px' }")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Website URL (Optional)
                    yz-text-field(v-model="rulesWebsiteValue" placeholder="https://example.com" type="url" :rules="websiteRules" clearable :style="{ maxWidth: '400px' }")
                .mt-4
                  .d-flex.gap-2
                    yz-btn(label="Reset" size="sm" @click="resetRulesValidation")
                    yz-btn(label="Validate" size="sm" outlined @click="validateRulesFields")
                  .mt-2(v-if="rulesValidationResult")
                    span.yz-regular-body-xs(:class="{ 'success--text': rulesValidationResult === 'Valid', 'error--text': rulesValidationResult === 'Invalid' }") {{ rulesValidationResult }}
                .mt-3
                  small.text--secondary Values: {{ JSON.stringify({ username: rulesUsernameValue, confirmPassword: '***', website: rulesWebsiteValue }) }}

            .variation-code
              .code-header.d-flex.align-center.mb-2
                span.font-weight-medium Code Examples

              .code-examples
                .mb-3
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === rulesTextFieldExampleCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === rulesTextFieldExampleCode ? 'success' : ''" @click="copyCode(rulesTextFieldExampleCode)")
                    span.font-weight-medium.ml-2 Custom Rules Example
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ rulesTextFieldExampleCode }}

          // Optional fields with conditional validation
          template(v-else-if="variation.id === 'optional-validation'")
            .variation-preview.mb-4.pa-4.border.rounded
              .mb-4
                h5.mb-2 Optional Fields with Smart Validation
                p.text--secondary.mb-3 These fields are optional but validate format when values are entered
                v-form(ref="optionalValidationForm")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Website (Optional)
                    yz-text-field(v-model="optionalWebsiteValue" placeholder="https://example.com" type="url" :rules="optionalWebsiteRules" clearable :style="{ maxWidth: '400px' }")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 LinkedIn Profile (Optional)
                    yz-text-field(v-model="optionalLinkedInValue" placeholder="https://linkedin.com/in/username" :rules="optionalLinkedInRules" clearable :style="{ maxWidth: '400px' }")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Secondary Phone (Optional)
                    yz-text-field(v-model="optionalPhoneValue" placeholder="Enter secondary phone..." type="tel" :rules="optionalPhoneRules" clearable :style="{ maxWidth: '400px' }")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Company Name (Optional)
                    yz-text-field(v-model="optionalCompanyValue" placeholder="Enter company name..." :rules="optionalCompanyRules" clearable :style="{ maxWidth: '400px' }")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Years of Experience (Optional)
                    yz-text-field(v-model="optionalExperienceValue" placeholder="Enter years..." type="number" :rules="optionalExperienceRules" clearable :style="{ maxWidth: '400px' }")
                .mt-4
                  .d-flex.gap-2
                    yz-btn(label="Reset" size="sm" @click="resetOptionalFields")
                    yz-btn(label="Validate" size="sm" outlined @click="validateOptionalFields")
                  .mt-2(v-if="optionalValidationResult")
                    span.yz-regular-body-xs(:class="{ 'success--text': optionalValidationResult === 'Valid', 'error--text': optionalValidationResult === 'Invalid' }") {{ optionalValidationResult }}
                .mt-3
                  small.text--secondary Values: {{ JSON.stringify({ website: optionalWebsiteValue, linkedIn: optionalLinkedInValue, phone: optionalPhoneValue, company: optionalCompanyValue, experience: optionalExperienceValue }) }}

            .variation-code
              .code-header.d-flex.align-center.mb-2
                span.font-weight-medium Code Examples

              .code-examples
                .mb-3
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === optionalValidationExampleCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalValidationExampleCode ? 'success' : ''" @click="copyCode(optionalValidationExampleCode)")
                    span.font-weight-medium.ml-2 Optional Validation Example
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ optionalValidationExampleCode }}

          // Individual text field
          template(v-else)
            .variation-preview.mb-4.pa-4.border.rounded
              .mb-2
                span.font-weight-medium {{ variation.label }}
                small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
              yz-text-field(v-model="variation.value" v-bind="variation.props" :style="variation.id === 'width-control' ? {} : { maxWidth: '400px' }")

            .variation-code
              .code-header.d-flex.align-center.mb-2
                yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
                span.font-weight-medium.ml-2 Code

              pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
                code {{ variation.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API

      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in textFieldProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      h4.mb-3 Events
      .events-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Parameters
                th Description
            tbody
              tr(v-for="event in textFieldEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.text--secondary.text-no-wrap {{ event.parameters }}
                td.single-line {{ event.description }}

      h4.mb-3 Slots
      .slots-info.mb-3.pa-3.rounded(style="background-color: #e3f2fd; border-left: 4px solid #2196f3;")
        p.mb-0.text--primary
          strong Note:
          | YzTextField supports slot passthrough for all v-text-field slots. Use slots when you need custom content beyond what props provide (like conditional icons, badges, or interactive buttons).
      .slots-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot Name
                th Parameters
                th Description
            tbody
              tr(v-for="slot in textFieldSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.text--secondary.text-no-wrap {{ slot.parameters }}
                td.single-line {{ slot.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      showPassword: false,
      fieldValues: {
        basic: '',
        disabled: 'Disabled value',
        readonly: 'This is read-only content',
        error: 'Invalid input',
        loading: 'query text',
        clearable: 'Text with clear button',
        autofocus: 'This field is focused on load',
        sizeLg: 'Large text field',
        sizeMd: 'Medium text field',
        sizeSm: 'Small text field',
        counterBasic: 'Hello world',
        counterMax: 'This text is limited',
        counterValidation: 'Valid text',
        textType: '',
        emailType: '',
        password: 'secretpassword',
        urlType: '',
        telType: '',
        searchType: '',
        numberType: null,
        dateType: '',
        timeType: '',
        datetimeType: '',
        prependIcon: '',
        appendIcon: 'Hello world',
        prependInner: '',
        appendOuter: 'Some text',
        bothIcons: 'user@example.com',
        prefix: '199.99',
        suffix: '75',
        prefixSuffix: '1299.99',
        combined: '999.50',
        colorDefault: 'Default white background',
        colorHex: 'Light gray background',
        colorYzToken: 'Light blue background',
        colorCssVar: 'Custom CSS variable color',
        colorShort: 'Neutral background',
        widthPixel: 'Fixed 300px width',
        widthPercent: '50% of container',
        widthFull: 'Full width',
        widthRem: '20rem width',
        widthFitContent: 'Fits content width',
        slotAppend: '202301012345',
        slotPrepend: 'john_doe',
        slotPrependInner: '',
        slotAppendOuter: '',
        slotMultiple: 'product',
        slotConditional: 'user@example.com'
      },
      // V-form validation data
      showVformPassword: false,
      vformNameValue: '',
      vformEmailValue: '',
      vformPasswordValue: 'password',
      vformPhoneValue: '',
      vformAgeValue: null,
      vformBirthDateValue: '',
      vformContactTimeValue: '',
      vformTextFieldValidationResult: null,
      // Rules-based validation data
      rulesUsernameValue: '',
      rulesConfirmPasswordValue: '',
      rulesWebsiteValue: '',
      rulesValidationResult: null,
      // Optional fields with conditional validation data
      optionalWebsiteValue: '',
      optionalLinkedInValue: '',
      optionalPhoneValue: '',
      optionalCompanyValue: '',
      optionalExperienceValue: null,
      optionalValidationResult: null,
      // Code examples for new sections
      vformTextFieldExampleCode: `// Template
v-form(ref="demoForm")
  .mb-3
    label Full Name
    yz-text-field(
      v-model="nameValue" 
      placeholder="Enter your full name..." 
      required 
      required-error-message="Full name is required"
      :rules="nameRules"
      clearable
    )
  .mb-3
    label Email Address
    yz-text-field(
      v-model="emailValue" 
      placeholder="Enter your email..." 
      type="email" 
      :rules="emailRules" 
      clearable
    )
  .mb-3
    label Password
    yz-text-field(
      v-model="passwordValue" 
      placeholder="Enter password..." 
      :type="showPassword ? 'text' : 'password'" 
      :append-icon="showPassword ? '$yzi-eye' : '$yzi-slash-circle-01'" 
      :rules="passwordRules" 
      clearable 
      @click:append="showPassword = !showPassword"
    )
  .mb-3
    label Tel type
    yz-text-field(
      v-model="phoneValue" 
      placeholder="Enter phone number..." 
      type="tel" 
      required
      required-error-message="Phone number is required"
      :rules="phoneRules"
      clearable
    )
  .mb-3
    label Age
    yz-text-field(
      v-model="ageValue" 
      placeholder="Enter your age..." 
      type="number" 
      required
      required-error-message="Age is required"
      :rules="ageRules" 
      clearable
    )
  .mb-3
    label Birth Date
    yz-text-field(
      v-model="birthDateValue" 
      type="date" 
      required
      required-error-message="Birth date is required"
      :rules="birthDateRules"
      clearable
    )
  .mb-3
    label Preferred Contact Time
    yz-text-field(
      v-model="contactTimeValue" 
      type="time" 
      required
      required-error-message="Contact time is required"
      :rules="contactTimeRules"
      clearable
    )
  .mt-4
    yz-btn(label="Validate Form" @click="validateForm")
    yz-btn(label="Reset Form" @click="resetForm")

// Script
data() {
  return {
    nameValue: '',
    emailValue: '',
    passwordValue: '',
    phoneValue: '',
    ageValue: null,
    birthDateValue: '',
    contactTimeValue: '',
    showPassword: false
  }
},
computed: {
  nameRules() {
    return [
      v => (v && v.length >= 2) || 'Name must be at least 2 characters'
    ]
  },
  emailRules() {
    return [
      v => /.+@.+\\..+/.test(v) || 'Email must be valid'
    ]
  },
  passwordRules() {
    return [
      v => (v && v.length >= 8) || 'Password must be at least 8 characters'
    ]
  },
  phoneRules() {
    return [
      v => (v && v.length >= 10) || 'Phone number must be at least 10 digits'
    ]
  },
  ageRules() {
    return [
      v => (v && v >= 18) || 'Must be 18 or older',
      v => (v && v <= 120) || 'Please enter a valid age'
    ]
  },
  birthDateRules() {
    return [
      v => (v && new Date(v) <= new Date()) || 'Birth date cannot be in the future'
    ]
  },
  contactTimeRules() {
    return [
      v => (v && v >= '09:00' && v <= '17:00') || 'Contact time must be between 9 AM and 5 PM'
    ]
  }
},
methods: {
  validateForm() {
    const isValid = this.$refs.demoForm.validate();
    console.log('Form is valid:', isValid);
  },
  resetForm() {
    this.$refs.demoForm.resetValidation();
  }
}`,
      rulesTextFieldExampleCode: `// Template
yz-text-field(
  v-model="usernameValue" 
  placeholder="Enter username..." 
  :rules="customUsernameRules" 
  clearable
)

yz-text-field(
  v-model="confirmPasswordValue" 
  placeholder="Confirm password..." 
  type="password" 
  :rules="confirmPasswordRules" 
  clearable
)

yz-text-field(
  v-model="websiteValue" 
  placeholder="https://example.com" 
  type="url" 
  :rules="websiteRules" 
  clearable
)

// Script
data() {
  return {
    usernameValue: '',
    confirmPasswordValue: '',
    websiteValue: '',
    passwordValue: 'password' // for password confirmation
  }
},
computed: {
  customUsernameRules() {
    return [
      v => !!v || 'Username is required',
      v => (v && v.length >= 3) || 'Username must be at least 3 characters',
      v => (v && v.length <= 20) || 'Username must be less than 20 characters',
      v => /^[a-zA-Z0-9_]+$/.test(v) || 'Username can only contain letters, numbers, and underscores'
    ]
  },
  confirmPasswordRules() {
    return [
      v => !!v || 'Please confirm your password',
      v => v === this.passwordValue || 'Passwords do not match'
    ]
  },
  websiteRules() {
    return [
      v => !v || /^https?:\\/\\/.+\\..+/.test(v) || 'Website must be a valid URL starting with https:// or http://'
    ]
  }
}`,
      optionalValidationExampleCode: `// Template
// Optional Website
yz-text-field(
  v-model="websiteValue" 
  placeholder="https://example.com" 
  type="url" 
  :rules="optionalWebsiteRules" 
  clearable
)

// Optional LinkedIn Profile
yz-text-field(
  v-model="linkedInValue" 
  placeholder="https://linkedin.com/in/username" 
  :rules="optionalLinkedInRules" 
  clearable
)

// Optional Secondary Phone
yz-text-field(
  v-model="phoneValue" 
  placeholder="Enter secondary phone..." 
  type="tel" 
  :rules="optionalPhoneRules" 
  clearable
)

// Optional Company Name
yz-text-field(
  v-model="companyValue" 
  placeholder="Enter company name..." 
  :rules="optionalCompanyRules" 
  clearable
)

// Optional Years of Experience
yz-text-field(
  v-model="experienceValue" 
  placeholder="Enter years..." 
  type="number" 
  :rules="optionalExperienceRules" 
  clearable
)

// Script
data() {
  return {
    websiteValue: '',
    linkedInValue: '',
    phoneValue: '',
    companyValue: '',
    experienceValue: null
  }
},
computed: {
  // Optional fields only validate when value is provided
  optionalWebsiteRules() {
    return [
      v => !v || /^https?:\\/\\/.+\\..+/.test(v) || 'Please enter a valid URL (http:// or https://)'
    ]
  },
  optionalLinkedInRules() {
    return [
      v => !v || /^https?:\\/\\/(www\\.)?linkedin\\.com\\/in\\//.test(v) || 'Please enter a valid LinkedIn profile URL'
    ]
  },
  optionalPhoneRules() {
    return [
      v => !v || v.length >= 10 || 'Phone number must be at least 10 digits'
    ]
  },
  optionalCompanyRules() {
    return [
      v => !v || v.length >= 2 || 'Company name must be at least 2 characters'
    ]
  },
  optionalExperienceRules() {
    return [
      v => !v || (v >= 0 && v <= 50) || 'Experience must be between 0 and 50 years'
    ]
  }
}`
    }
  },
  computed: {
    textFieldVariations() {
      return [
        {
          name: 'Basic States',
          id: 'basic-text-fields',
          description: 'Standard text field states including normal, disabled, read-only, and error',
          isGroup: true,
          fields: [
            {
              label: 'Basic',
              key: 'basic',
              props: {
                placeholder: 'Enter text here...'
              },
              value: this.fieldValues.basic,
              code: `yz-text-field(v-model="value" placeholder="Enter text here...")`,
              hint: 'Standard text field'
            },
            {
              label: 'Disabled',
              key: 'disabled',
              props: {
                placeholder: 'This field is disabled',
                disabled: true
              },
              value: this.fieldValues.disabled,
              code: `yz-text-field(v-model="value" placeholder="This field is disabled" disabled)`,
              hint: 'Cannot be edited'
            },
            {
              label: 'Read-only',
              key: 'readonly',
              props: {
                readonly: true
              },
              value: this.fieldValues.readonly,
              code: `yz-text-field(v-model="value" readonly)`,
              hint: 'Cannot be modified'
            },
            {
              label: 'Error State',
              key: 'error',
              props: {
                placeholder: 'Enter valid text...',
                errorMessages: ['This field is required', 'Please enter a valid value'],
                error: true
              },
              value: this.fieldValues.error,
              code: `yz-text-field(v-model="value" placeholder="Enter valid text..." :error-messages="['This field is required', 'Please enter a valid value']" error)`,
              hint: 'Shows error state'
            },
            {
              label: 'Loading',
              key: 'loading',
              props: {
                placeholder: 'Searching...',
                loading: true
              },
              value: this.fieldValues.loading,
              code: `yz-text-field(v-model="value" placeholder="Searching..." loading)`,
              hint: 'Shows loading indicator'
            },
            {
              label: 'Clearable',
              key: 'clearable',
              props: {
                placeholder: 'Enter text to clear...',
                clearable: true
              },
              value: this.fieldValues.clearable,
              code: `yz-text-field(v-model="value" placeholder="Enter text to clear..." clearable)`,
              hint: 'Shows clear button when text exists'
            },
            {
              label: 'Autofocus',
              key: 'autofocus',
              props: {
                placeholder: 'This field is auto-focused...',
                autofocus: true
              },
              value: this.fieldValues.autofocus,
              code: `yz-text-field(v-model="value" placeholder="This field is auto-focused..." autofocus)`,
              hint: 'Automatically focuses on page load'
            },
          ]
        },
        {
          name: 'Sizes',
          id: 'sizes',
          description: 'Different text field sizes for various use cases',
          isGroup: true,
          fields: [
            {
              label: 'Large (36px)',
              key: 'sizeLg',
              props: {
                placeholder: 'Large text field...',
                size: 'lg'
              },
              value: this.fieldValues.sizeLg,
              code: `yz-text-field(v-model="value" placeholder="Large text field..." size="lg")`,
              hint: 'Default size - 36px height'
            },
            {
              label: 'Medium (32px)',
              key: 'sizeMd',
              props: {
                placeholder: 'Medium text field...',
                size: 'md'
              },
              value: this.fieldValues.sizeMd,
              code: `yz-text-field(v-model="value" placeholder="Medium text field..." size="md")`,
              hint: 'Medium size - 32px height'
            },
            {
              label: 'Small (28px)',
              key: 'sizeSm',
              props: {
                placeholder: 'Small text field...',
                size: 'sm'
              },
              value: this.fieldValues.sizeSm,
              code: `yz-text-field(v-model="value" placeholder="Small text field..." size="sm")`,
              hint: 'Small size - 28px height'
            }
          ]
        },
        {
          name: 'Input Types',
          id: 'input-types',
          description: 'Different input types for various data entry scenarios',
          isGroup: true,
          fields: [
            {
              label: 'Text (Default)',
              key: 'textType',
              props: {
                placeholder: 'Enter any text...',
                type: 'text',
                clearable: true
              },
              value: this.fieldValues.textType,
              code: `yz-text-field(v-model="value" placeholder="Enter any text..." type="text" clearable)`,
              hint: 'Standard text input'
            },
            {
              label: 'Email',
              key: 'emailType',
              props: {
                placeholder: 'Enter email address...',
                type: 'email',
                clearable: true
              },
              value: this.fieldValues.emailType,
              code: `yz-text-field(v-model="value" placeholder="Enter email address..." type="email" clearable)`,
              hint: 'Email input with validation'
            },
            {
              label: 'Password',
              key: 'password',
              props: {
                placeholder: 'Enter password...',
                appendIcon: this.showPassword ? '$yzi-eye' : '$yzi-slash-circle-01',
                type: this.showPassword ? 'text' : 'password',
                clearable: true
              },
              value: this.fieldValues.password,
              code: `yz-text-field(v-model="value" :append-icon="show ? '$yzi-eye' : '$yzi-slash-circle-01'" :type="show ? 'text' : 'password'" placeholder="Enter password..." clearable @click:append="show = !show")`,
              hint: 'Toggleable password visibility',
              clickHandler: () => {
                this.showPassword = !this.showPassword
              }
            },
            {
              label: 'URL',
              key: 'urlType',
              props: {
                placeholder: 'https://example.com',
                type: 'url',
                clearable: true
              },
              value: this.fieldValues.urlType,
              code: `yz-text-field(v-model="value" placeholder="https://example.com" type="url" clearable)`,
              hint: 'URL input with validation'
            },
            {
              label: 'Phone',
              key: 'telType',
              props: {
                placeholder: 'Enter phone number...',
                type: 'tel',
                clearable: true
              },
              value: this.fieldValues.telType,
              code: `yz-text-field(v-model="value" placeholder="Enter phone number..." type="tel" clearable)`,
              hint: 'Telephone number input'
            },
            {
              label: 'Search',
              key: 'searchType',
              props: {
                placeholder: 'Search...',
                type: 'search',
                clearable: true,
                prependInnerIcon: '$yzi-search-sm'
              },
              value: this.fieldValues.searchType,
              code: `yz-text-field(v-model="value" placeholder="Search..." type="search" clearable prepend-inner-icon="$yzi-search-sm")`,
              hint: 'Search input with icon'
            },
            {
              label: 'Number',
              key: 'numberType',
              props: {
                placeholder: 'Enter a number...',
                type: 'number',
                clearable: true
              },
              value: this.fieldValues.numberType,
              code: `yz-text-field(v-model="value" placeholder="Enter a number..." type="number" clearable)`,
              hint: 'Numeric input with controls'
            },
            {
              label: 'Date',
              key: 'dateType',
              props: {
                type: 'date',
                clearable: true
              },
              value: this.fieldValues.dateType,
              code: `yz-text-field(v-model="value" type="date" clearable)`,
              hint: 'Date picker input'
            },
            {
              label: 'Time',
              key: 'timeType',
              props: {
                type: 'time',
                clearable: true
              },
              value: this.fieldValues.timeType,
              code: `yz-text-field(v-model="value" type="time" clearable)`,
              hint: 'Time picker input'
            },
            {
              label: 'DateTime',
              key: 'datetimeType',
              props: {
                type: 'datetime-local',
                clearable: true
              },
              value: this.fieldValues.datetimeType,
              code: `yz-text-field(v-model="value" type="datetime-local" clearable)`,
              hint: 'Date and time picker input'
            }
          ]
        },
        {
          name: 'Counter',
          id: 'counter',
          description: 'Text fields with character counting functionality',
          isGroup: true,
          fields: [
            {
              label: 'Basic Counter',
              key: 'counterBasic',
              props: {
                placeholder: 'Type to see character count...',
                counter: true
              },
              value: this.fieldValues.counterBasic,
              code: `yz-text-field(v-model="value" placeholder="Type to see character count..." counter)`,
              hint: 'Shows current character count'
            },
            {
              label: 'Counter with Max Length',
              key: 'counterMax',
              props: {
                placeholder: 'Maximum 25 characters...',
                counter: 25,
                maxlength: 25
              },
              value: this.fieldValues.counterMax,
              code: `yz-text-field(v-model="value" placeholder="Maximum 25 characters..." :counter="25" maxlength="25")`,
              hint: 'Shows count with maximum limit'
            },
            {
              label: 'Counter with Validation',
              key: 'counterValidation',
              props: {
                placeholder: 'Enter 10-20 characters...',
                counter: 20,
                rules: [
                  (v) => (v && v.length >= 10) || 'Minimum 10 characters',
                  (v) => (v && v.length <= 20) || 'Maximum 20 characters'
                ]
              },
              value: this.fieldValues.counterValidation,
              code: `yz-text-field(v-model="value" placeholder="Enter 10-20 characters..." :counter="20" :rules="[v => (v && v.length >= 10) || 'Minimum 10 characters', v => (v && v.length <= 20) || 'Maximum 20 characters']")`,
              hint: 'Counter with validation rules'
            }
          ]
        },
        {
          name: 'Icons & Affixes',
          id: 'icons-affixes',
          description: 'Text fields with icons, prefixes, and suffixes for enhanced functionality',
          isGroup: true,
          fields: [
            {
              label: 'Prepend Icon',
              key: 'prependIcon',
              props: {
                placeholder: 'Search something...',
                prependIcon: '$yzi-search-sm'
              },
              value: this.fieldValues.prependIcon,
              code: `yz-text-field(v-model="value" placeholder="Search something..." prepend-icon="$yzi-search-sm")`,
              hint: 'Icon outside the input border'
            },
            {
              label: 'Append Icon',
              key: 'appendIcon',
              props: {
                placeholder: 'Enter text...',
                appendIcon: '$yzi-edit-02'
              },
              value: this.fieldValues.appendIcon,
              code: `yz-text-field(v-model="value" placeholder="Enter text..." append-icon="$yzi-edit-02")`,
              hint: 'Icon outside the input border'
            },
            {
              label: 'Prepend Inner Icon',
              key: 'prependInner',
              props: {
                placeholder: 'Search inside...',
                prependInnerIcon: '$yzi-search-sm'
              },
              value: this.fieldValues.prependInner,
              code: `yz-text-field(v-model="value" placeholder="Search inside..." prepend-inner-icon="$yzi-search-sm")`,
              hint: 'Icon inside the input border'
            },
            {
              label: 'Append Outer Icon',
              key: 'appendOuter',
              props: {
                placeholder: 'Text with outer icon...',
                appendOuterIcon: '$yzi-settings-02'
              },
              value: this.fieldValues.appendOuter,
              code: `yz-text-field(v-model="value" placeholder="Text with outer icon..." append-outer-icon="$yzi-settings-02")`,
              hint: 'Icon in outer append slot'
            },
            {
              label: 'Both Icons',
              key: 'bothIcons',
              props: {
                placeholder: 'Enter email...',
                prependIcon: '$yzi-mail-01',
                appendIcon: '$yzi-check-circle'
              },
              value: this.fieldValues.bothIcons,
              code: `yz-text-field(v-model="value" placeholder="Enter email..." prepend-icon="$yzi-mail-01" append-icon="$yzi-check-circle")`,
              hint: 'Icons on both sides'
            },
            {
              label: 'Prefix',
              key: 'prefix',
              props: {
                placeholder: '0.00',
                prefix: '$'
              },
              value: this.fieldValues.prefix,
              code: `yz-text-field(v-model="value" placeholder="0.00" prefix="$")`,
              hint: 'Text prefix inside input'
            },
            {
              label: 'Suffix',
              key: 'suffix',
              props: {
                placeholder: 'Enter weight...',
                suffix: 'kg'
              },
              value: this.fieldValues.suffix,
              code: `yz-text-field(v-model="value" placeholder="Enter weight..." suffix="kg")`,
              hint: 'Text suffix inside input'
            },
            {
              label: 'Prefix & Suffix',
              key: 'prefixSuffix',
              props: {
                placeholder: '0.00',
                prefix: '$',
                suffix: 'USD'
              },
              value: this.fieldValues.prefixSuffix,
              code: `yz-text-field(v-model="value" placeholder="0.00" prefix="$" suffix="USD")`,
              hint: 'Both prefix and suffix'
            },
            {
              label: 'Combined',
              key: 'combined',
              props: {
                placeholder: 'Enter amount...',
                prependIcon: '$yzi-coins-hand',
                appendIcon: '$yzi-calculator',
                prefix: '$',
                suffix: 'USD'
              },
              value: this.fieldValues.combined,
              code: `yz-text-field(v-model="value" placeholder="Enter amount..." prepend-icon="$yzi-coins-hand" append-icon="$yzi-calculator" prefix="$" suffix="USD")`,
              hint: 'Icons with prefix and suffix'
            }
          ]
        },
        {
          name: 'Background Colors',
          id: 'background-colors',
          description: 'Text fields with custom background colors supporting multiple color formats',
          isGroup: true,
          fields: [
            {
              label: 'Default (White)',
              key: 'colorDefault',
              props: {
                placeholder: 'Default white background...',
                backgroundColor: 'white'
              },
              value: this.fieldValues.colorDefault,
              code: `yz-text-field(v-model="value" placeholder="Default white background..." background-color="white")`,
              hint: 'Default white background color'
            },
            {
              label: 'Hex Color',
              key: 'colorHex',
              props: {
                placeholder: 'Light gray background...',
                backgroundColor: '#f8f9fa'
              },
              value: this.fieldValues.colorHex,
              code: `yz-text-field(v-model="value" placeholder="Light gray background..." background-color="#f8f9fa")`,
              hint: 'Using hex color format (#f8f9fa)'
            },
            {
              label: 'YZ Design Token',
              key: 'colorYzToken',
              props: {
                placeholder: 'Light blue background...',
                backgroundColor: 'yz-blue-100'
              },
              value: this.fieldValues.colorYzToken,
              code: `yz-text-field(v-model="value" placeholder="Light blue background..." background-color="yz-blue-100")`,
              hint: 'Using YZ design token (yz-blue-100)'
            },
            {
              label: 'CSS Variable',
              key: 'colorCssVar',
              props: {
                placeholder: 'CSS variable background...',
                backgroundColor: 'var(--yz-green-100)'
              },
              value: this.fieldValues.colorCssVar,
              code: `yz-text-field(v-model="value" placeholder="CSS variable background..." background-color="var(--yz-green-100)")`,
              hint: 'Using CSS variable format'
            },
            {
              label: 'Short Token',
              key: 'colorShort',
              props: {
                placeholder: 'Neutral background...',
                backgroundColor: 'neutral-200'
              },
              value: this.fieldValues.colorShort,
              code: `yz-text-field(v-model="value" placeholder="Neutral background..." background-color="neutral-200")`,
              hint: 'Using short design token (neutral-200)'
            }
          ]
        },
        {
          name: 'Width Control',
          id: 'width-control',
          description: 'Text fields with various width configurations using CSS units and predefined sizes',
          isGroup: true,
          fields: [
            {
              label: 'Fixed Pixel Width (300px)',
              key: 'widthPixel',
              props: {
                placeholder: 'Fixed 300px width...',
                width: '300px'
              },
              value: this.fieldValues.widthPixel,
              code: `yz-text-field(v-model="value" placeholder="Fixed 300px width..." width="300px")`,
              hint: 'Fixed width using pixels'
            },
            {
              label: 'Percentage Width (50%)',
              key: 'widthPercent',
              props: {
                placeholder: '50% of container width...',
                width: '50%'
              },
              value: this.fieldValues.widthPercent,
              code: `yz-text-field(v-model="value" placeholder="50% of container width..." width="50%")`,
              hint: 'Responsive width as percentage'
            },
            {
              label: 'Full Width',
              key: 'widthFull',
              props: {
                placeholder: 'Takes full container width...',
                width: 'full'
              },
              value: this.fieldValues.widthFull,
              code: `yz-text-field(v-model="value" placeholder="Takes full container width..." width="full")`,
              hint: 'Predefined full width (100%)'
            },
            {
              label: 'Rem Units (20rem)',
              key: 'widthRem',
              props: {
                placeholder: '20rem width...',
                width: '20rem'
              },
              value: this.fieldValues.widthRem,
              code: `yz-text-field(v-model="value" placeholder="20rem width..." width="20rem")`,
              hint: 'Width using rem units'
            },
            {
              label: 'Fit Content',
              key: 'widthFitContent',
              props: {
                placeholder: 'Short',
                width: 'fit-content'
              },
              value: this.fieldValues.widthFitContent,
              code: `yz-text-field(v-model="value" placeholder="Short" width="fit-content")`,
              hint: 'Width adjusts to content size'
            }
          ]
        },
        {
          name: 'Slots',
          id: 'slots',
          description: 'Custom slot content for advanced use cases like custom icons, validation indicators, and interactive elements',
          isGroup: true,
          fields: [
            {
              label: 'Append Slot - Verification Icon',
              key: 'slotAppend',
              props: {
                placeholder: 'Enter SSM number...',
                clearable: true
              },
              value: this.fieldValues.slotAppend,
              code: `// Template
yz-text-field(v-model="ssmNumber" placeholder="Enter SSM number..." clearable)
  template(#append)
    yz-icon(v-if="verified" color="yz-success-500") $yzi-check-circle

// Script
data() {
  return {
    ssmNumber: '202301012345',
    verified: true
  }
}`,
              hint: 'Custom icon in append slot'
            },
            {
              label: 'Prepend Slot - User Icon',
              key: 'slotPrepend',
              props: {
                placeholder: 'Enter username...',
                clearable: true
              },
              value: this.fieldValues.slotPrepend,
              code: `// Template
yz-text-field(v-model="username" placeholder="Enter username..." clearable)
  template(#prepend)
    yz-icon(color="yz-neutral-500") $yzi-user-01

// Script
data() {
  return {
    username: 'john_doe'
  }
}`,
              hint: 'Displays a user icon outside the input field (left side)'
            },
            {
              label: 'Prepend Inner Slot - Search Icon',
              key: 'slotPrependInner',
              props: {
                placeholder: 'Search products...',
                clearable: true
              },
              value: this.fieldValues.slotPrependInner,
              code: `// Template
yz-text-field(v-model="searchQuery" placeholder="Search products..." clearable)
  template(#prepend-inner)
    yz-icon(color="yz-neutral-500") $yzi-search-sm

// Script
data() {
  return {
    searchQuery: ''
  }
}`,
              hint: 'Icon inside the input field'
            },
            {
              label: 'Append Outer Slot - Action Button',
              key: 'slotAppendOuter',
              props: {
                placeholder: 'Enter notes...',
                clearable: true
              },
              value: this.fieldValues.slotAppendOuter,
              code: `// Template
yz-text-field(v-model="notes" placeholder="Enter notes..." clearable)
  template(#append-outer)
    yz-btn(icon-only size="sm" icon="$yzi-plus" @click="addNote")

// Script
data() {
  return {
    notes: ''
  }
},
methods: {
  addNote() {
    console.log('Adding note:', this.notes)
  }
}`,
              hint: 'Interactive button in outer append area'
            },
            {
              label: 'Multiple Slots Combined',
              key: 'slotMultiple',
              props: {
                placeholder: 'Search...',
                clearable: true
              },
              value: this.fieldValues.slotMultiple,
              code: `// Template
yz-text-field(v-model="searchValue" placeholder="Search..." clearable)
  template(#prepend-inner)
    yz-icon(color="yz-neutral-500") $yzi-search-sm
  template(#append)
    yz-icon(v-if="hasResults" color="yz-success-500") $yzi-check-circle
  template(#append-outer)
    yz-btn(icon-only size="sm" icon="$yzi-filter-lines" @click="toggleFilters")

// Script
data() {
  return {
    searchValue: 'product',
    hasResults: true
  }
},
methods: {
  toggleFilters() {
    console.log('Toggle filters')
  }
}`,
              hint: 'Multiple slots working together'
            },
            {
              label: 'Conditional Slot Content',
              key: 'slotConditional',
              props: {
                placeholder: 'Enter email...',
                type: 'email',
                clearable: true
              },
              value: this.fieldValues.slotConditional,
              code: `// Template
yz-text-field(v-model="email" placeholder="Enter email..." type="email" clearable :rules="emailRules")
  template(#append)
    yz-icon(v-if="isValidEmail" color="yz-success-500") $yzi-check-circle
    yz-icon(v-else-if="email && !isValidEmail" color="yz-danger-500") $yzi-x-circle

// Script
data() {
  return {
    email: 'user@example.com'
  }
},
computed: {
  isValidEmail() {
    return this.email && /.+@.+\\..+/.test(this.email)
  },
  emailRules() {
    return [
      v => /.+@.+\\..+/.test(v) || 'Email must be valid'
    ]
  }
}`,
              hint: 'Dynamic icons based on validation state'
            }
          ]
        },
        {
          name: 'V-form Integration',
          id: 'vform-integration',
          description: 'Using YzTextField with Vuetify v-form for comprehensive form validation',
          isGroup: false
        },
        {
          name: 'Rules-based Validation',
          id: 'rules-validation',
          description: 'Custom validation rules with the rules prop for advanced validation scenarios',
          isGroup: false
        },
        {
          name: 'Optional Fields with Conditional Validation',
          id: 'optional-validation',
          description: 'Optional fields that validate only when a value is provided - useful for optional profile fields',
          isGroup: false
        }
      ]
    },
    textFieldProps() {
      return [
        {
          name: 'value',
          type: 'String',
          default: 'null',
          description: 'The input value (v-model)'
        },
        {
          name: 'placeholder',
          type: 'String',
          default: 'null',
          description: 'Placeholder text'
        },
        {
          name: 'disabled',
          type: 'Boolean',
          default: 'false',
          description: 'Disables the text field'
        },
        {
          name: 'readonly',
          type: 'Boolean',
          default: 'false',
          description: 'Makes the text field read-only'
        },
        {
          name: 'error',
          type: 'Boolean',
          default: 'false',
          description: 'Shows error state styling'
        },
        {
          name: 'errorMessages',
          type: 'Array',
          default: '[]',
          description: 'Array of error messages to display'
        },
        {
          name: 'rules',
          type: 'Array',
          default: '[]',
          description: 'Array of validation rules'
        },
        {
          name: 'required',
          type: 'Boolean',
          default: 'false',
          description: 'Makes the text field required. Automatically adds validation rule. Disabled when field is disabled.'
        },
        {
          name: 'requiredErrorMessage',
          type: 'String',
          default: 'This field is required',
          description: 'Custom error message displayed when required field is empty'
        },
        {
          name: 'hideDetails',
          type: 'Boolean | String',
          default: 'auto',
          description: 'Hide error messages and hints. Can be true, false, or "auto"'
        },
        {
          name: 'type',
          type: 'String',
          default: 'text',
          description: 'Input type (text, password, email, etc.)'
        },
        {
          name: 'hint',
          type: 'String',
          default: 'null',
          description: 'Hint text displayed below the field'
        },
        {
          name: 'prependIcon',
          type: 'String',
          default: 'null',
          description: 'Icon displayed outside the input field border (before)'
        },
        {
          name: 'appendIcon',
          type: 'String',
          default: 'null',
          description: 'Icon displayed outside the input field border (after)'
        },
        {
          name: 'prependInnerIcon',
          type: 'String',
          default: 'null',
          description: 'Icon displayed inside the input field border (before)'
        },
        {
          name: 'appendOuterIcon',
          type: 'String',
          default: 'null',
          description: 'Icon displayed in the outer append slot (far right)'
        },
        {
          name: 'prefix',
          type: 'String',
          default: 'null',
          description: 'Text prefix displayed inside the input'
        },
        {
          name: 'suffix',
          type: 'String',
          default: 'null',
          description: 'Text suffix displayed inside the input'
        },
        {
          name: 'loading',
          type: 'Boolean',
          default: 'false',
          description: 'Shows loading indicator in the input field'
        },
        {
          name: 'clearable',
          type: 'Boolean',
          default: 'false',
          description: 'Shows clear button when input has content'
        },
        {
          name: 'autofocus',
          type: 'Boolean',
          default: 'false',
          description: 'Automatically focuses the input field on page load'
        },
        {
          name: 'counter',
          type: 'Boolean | Number',
          default: 'false',
          description: 'Shows character count. Use number to set max length'
        },
        {
          name: 'maxlength',
          type: 'Number | String',
          default: 'null',
          description: 'Sets the maximum number of characters allowed'
        },
        {
          name: 'size',
          type: 'String',
          default: 'lg',
          description: 'Sets the text field size. Options: lg (36px), md (32px), sm (28px)'
        },
        {
          name: 'backgroundColor',
          type: 'String',
          default: 'white',
          description: 'Sets the background color of the text field. Supports multiple formats: hex (#ffffff), CSS variables (var(--yz-color)), YZ design tokens (yz-neutral-100), and short tokens (neutral-100)'
        },
        {
          name: 'width',
          type: 'String',
          default: 'auto',
          description: 'Sets the width of the text field. Supports CSS units (px, %, rem, em, vw, vh), predefined sizes (full, fit-content), or auto for default width'
        },
        {
          name: 'clickable',
          type: 'Boolean',
          default: 'false',
          description: 'When true and readonly is set, changes cursor from not-allowed to pointer. Useful for readonly fields that trigger actions like date pickers or dropdowns'
        }
      ]
    },
    textFieldEvents() {
      return [
        {
          name: '@input',
          parameters: 'value',
          description: 'Emitted when the input value changes'
        },
        {
          name: '@click',
          parameters: 'event',
          description: 'Emitted when the input field is clicked'
        },
        {
          name: '@click:append',
          parameters: 'event',
          description: 'Emitted when the append icon is clicked'
        },
        {
          name: '@click:prepend',
          parameters: 'event',
          description: 'Emitted when the prepend icon is clicked'
        },
        {
          name: '@focus',
          parameters: 'event',
          description: 'Emitted when the input field gains focus'
        },
        {
          name: '@blur',
          parameters: 'event',
          description: 'Emitted when the input field loses focus'
        },
        {
          name: '@keydown',
          parameters: 'event',
          description: 'Emitted when a key is pressed down'
        },
        {
          name: '@keyup',
          parameters: 'event',
          description: 'Emitted when a key is released'
        }
      ]
    },
    textFieldSlots() {
      return [
        {
          name: 'append',
          parameters: 'none',
          description: 'Slot for custom content inside the input field, aligned to the right (after the input text). Ideal for conditional icons, validation indicators, or status badges.'
        },
        {
          name: 'append-outer',
          parameters: 'none',
          description: 'Slot for custom content outside the input field border, positioned after the field. Perfect for action buttons, help icons, or interactive controls.'
        },
        {
          name: 'prepend',
          parameters: 'none',
          description: 'Slot for custom content outside the input field border, positioned before the field. Use for labels, badges, or decorative elements.'
        },
        {
          name: 'prepend-inner',
          parameters: 'none',
          description: 'Slot for custom content inside the input field, aligned to the left (before the input text). Great for search icons, currency symbols, or input type indicators.'
        }
      ]
    },
    customUsernameRules() {
      return [
        v => !!v || 'Username is required',
        v => (v && v.length >= 3) || 'Username must be at least 3 characters',
        v => (v && v.length <= 20) || 'Username must be less than 20 characters',
        v => /^[a-zA-Z0-9_]+$/.test(v) || 'Username can only contain letters, numbers, and underscores'
      ]
    },
    confirmPasswordRules() {
      return [
        v => !!v || 'Please confirm your password',
        v => v === this.vformPasswordValue || 'Passwords do not match'
      ]
    },
    websiteRules() {
      return [
        v => !v || /^https?:\/\/.+\..+/.test(v) || 'Website must be a valid URL starting with http:// or https://'
      ]
    },
    // Optional validation rules - only validate when value is provided
    optionalWebsiteRules() {
      return [
        v => !v || /^https?:\/\/.+\..+/.test(v) || 'Please enter a valid URL (http:// or https://)'
      ]
    },
    optionalLinkedInRules() {
      return [
        v => !v || /^https?:\/\/(www\.)?linkedin\.com\/in\//.test(v) || 'Please enter a valid LinkedIn profile URL'
      ]
    },
    optionalPhoneRules() {
      return [
        v => !v || v.length >= 10 || 'Phone number must be at least 10 digits'
      ]
    },
    optionalCompanyRules() {
      return [
        v => !v || v.length >= 2 || 'Company name must be at least 2 characters'
      ]
    },
    optionalExperienceRules() {
      return [
        v => !v || (v >= 0 && v <= 50) || 'Experience must be between 0 and 50 years'
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      // Update the route with the section hash
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    },
    validateTextFieldVForm() {
      if (this.$refs.demoTextFieldForm) {
        const demoFormArray = this.$refs.demoTextFieldForm;
        const demoFormRef = Array.isArray(demoFormArray) ? demoFormArray[0] : demoFormArray;
        const isValid = demoFormRef.validate();
        this.vformTextFieldValidationResult = isValid ? 'Valid' : 'Invalid';
        setTimeout(() => {
          this.vformTextFieldValidationResult = null;
        }, 1500);
      }
    },
    resetTextFieldVForm() {
      if (this.$refs.demoTextFieldForm) {
        const demoFormArray = this.$refs.demoTextFieldForm;
        const demoFormRef = Array.isArray(demoFormArray) ? demoFormArray[0] : demoFormArray;
        demoFormRef.reset();
        this.vformTextFieldValidationResult = null;
      }
    },
    validateRulesFields() {
      if (this.$refs.rulesValidationForm) {
        const rulesFormArray = this.$refs.rulesValidationForm;
        const rulesFormRef = Array.isArray(rulesFormArray) ? rulesFormArray[0] : rulesFormArray;
        const isValid = rulesFormRef.validate();
        this.rulesValidationResult = isValid ? 'Valid' : 'Invalid';
        setTimeout(() => {
          this.rulesValidationResult = null;
        }, 2000);
      }
    },
    resetRulesValidation() {
      if (this.$refs.rulesValidationForm) {
        const rulesFormArray = this.$refs.rulesValidationForm;
        const rulesFormRef = Array.isArray(rulesFormArray) ? rulesFormArray[0] : rulesFormArray;
        rulesFormRef.reset();
        this.rulesValidationResult = null;
      }
    },
    validateOptionalFields() {
      if (this.$refs.optionalValidationForm) {
        const optionalFormArray = this.$refs.optionalValidationForm;
        const optionalFormRef = Array.isArray(optionalFormArray) ? optionalFormArray[0] : optionalFormArray;
        const isValid = optionalFormRef.validate();
        this.optionalValidationResult = isValid ? 'Valid' : 'Invalid';
        setTimeout(() => {
          this.optionalValidationResult = null;
        }, 2000);
      }
    },
    resetOptionalFields() {
      if (this.$refs.optionalValidationForm) {
        const optionalFormArray = this.$refs.optionalValidationForm;
        const optionalFormRef = Array.isArray(optionalFormArray) ? optionalFormArray[0] : optionalFormArray;
        optionalFormRef.reset();
        this.optionalValidationResult = null;
      }
    },
    handleSlotAction(action) {
      console.log(`Slot action triggered: ${action}`)
    },
    isValidEmail(email) {
      return email && /.+@.+\..+/.test(email)
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.gap-4 > * + * {
  margin-top: 1rem;
}
</style>